import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { conditionGroupChildenKey } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import { useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import getPlugin from 'editor/src/store/plugins/selector/getPlugin';
import { PluginName, PluginState } from 'editor/src/store/plugins/types';

import ConditionGroupBuilder from 'editor/src/component/ConditionGroupBuilder';
import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import ButtonAddLayer from 'editor/src/component/DesktopSidebar/TabContents/LayersTabContent/ButtonAddLayer';
import TabContentHeader from 'editor/src/component/DesktopSidebar/TabContents/TabContentHeader';
import ReflowGroupsList from 'editor/src/component/ReflowGroupBuilder/ReflowGroupsList';

import DrawerHeader from '../../../BottomBarMobile/DrawerHeader';
import { useIsMobile } from '../../../useDetectDeviceType';

import AIToggle from './AIToggle';
import CustomerSettings from './CustomerSettings';
import DefaultPersonalizationPlugin from './DefaultPersonalizationPlugin';
import EmailWorkflowPersonalizationOption from './EmailWorkflowPersonalizationOption';
import InPagePersonalizationOptions from './InPagePersonalizationOptions';
import NonPersonalizableElements from './NonPersonalizableElements';
import PersonalizationSettingsMenu from './PersonalizationSettingsMenu';
import PrintFileUploadSetting from './PrintFileUploadSetting';
import SharedValuesSection from './SharedValuesSection';
import ShopifyPersonalizationOptions from './ShopifyPersonalizationOptions';

import styles from './index.module.scss';

interface Props {
  closeMenuCb?: () => void;
}

function PersonalizationTabContent({ closeMenuCb }: Props) {
  const { t } = useTranslation();
  const defaultPersonalizationPlugin = useSelector((state) => getHostSetting(state, 'defaultPersonalizationPlugin'));
  const isMobile = useIsMobile();

  const {
    conditionGroup,
    aiPersonalization,
    aiPersonalizationOn,
    shopifyPersonalization,
    shopifyInPagePersonalizationON,
    shopifyPersonalizationInEditor,
    emailWorkflowPersonalization,
    personalizationStudio,
    personalizationPlugin,
    inPagePersonalization,
    inPagePersonalizationON,
  } = useSelector((state) => {
    const spreadIndex = getCurrentSpreadIndex(state);
    return {
      conditionGroup: state.design.designData?.spreads[spreadIndex]?.conditionGroup,
      aiPersonalization: !!getPlugin(state, PluginName.AIPersonalization),
      aiPersonalizationOn: state.design.globalPlugins.includes(PluginName.AIPersonalization),
      shopifyPersonalization: !!getPlugin(state, PluginName.shopifyPersonalization),
      shopifyInPagePersonalizationON: state.design.globalPlugins.includes(PluginName.shopifyPersonalization),
      shopifyPersonalizationInEditor: !!getPlugin(state, PluginName.shopifyPersonalizationInEditor),
      emailWorkflowPersonalization: !!getPlugin(state, PluginName.emailWorkflowPersonalization),
      personalizationStudio: !!getPlugin(state, PluginName.personalizationStudio),
      personalizationPlugin: getPlugin(state, PluginName.personalizationStudio),
      inPagePersonalization: !!getPlugin(state, PluginName.inPagePersonalization),
      inPagePersonalizationON: state.design.globalPlugins.includes(PluginName.inPagePersonalization),
    };
  }, shallowEqual);

  const personalizedElements = conditionGroup?.children[conditionGroupChildenKey];

  const isShopifyConnected = useMemo(
    () => shopifyPersonalization && shopifyPersonalizationInEditor,
    [shopifyPersonalization, shopifyPersonalizationInEditor],
  );

  const isEtsyConnected = useMemo(
    () => aiPersonalization && emailWorkflowPersonalization,
    [aiPersonalization, emailWorkflowPersonalization],
  );

  const isTemplateFlow = useMemo(
    () => defaultPersonalizationPlugin && personalizationStudio && (isShopifyConnected || isEtsyConnected),
    [defaultPersonalizationPlugin, personalizationStudio, isShopifyConnected, isEtsyConnected],
  );

  const showCustomerSettings = !aiPersonalizationOn && !shopifyInPagePersonalizationON && !inPagePersonalizationON;

  const showCollection =
    personalizationPlugin?.state === PluginState.ENABLED && !personalizationPlugin?.extra?.hideCollections;

  const inlinePersonalizationOptions = (() => {
    if (aiPersonalization) {
      return <AIToggle closeMenuCb={closeMenuCb} />;
    }

    if (emailWorkflowPersonalization) {
      return <EmailWorkflowPersonalizationOption />;
    }

    return shopifyPersonalization ? <ShopifyPersonalizationOptions /> : null;
  })();

  return (
    <div className={cn(styles.nonPersonalizedItems, { [styles.mobile]: isMobile })}>
      {isMobile ? (
        <DrawerHeader title={t('Personalization')} closeMenu={closeMenuCb} />
      ) : (
        <TabContentHeader title={t('Personalization')} />
      )}

      <PrintFileUploadSetting />

      {isTemplateFlow ? (
        (!aiPersonalizationOn || !shopifyInPagePersonalizationON) && (
          <DefaultPersonalizationPlugin isEtsyConnected isShopifyConnected />
        )
      ) : (
        <>
          {inlinePersonalizationOptions}
          {(() => {
            const showInPageType = inPagePersonalization;

            if (!showInPageType && !showCustomerSettings) {
              return null;
            }

            return (
              <PersonalizationSettingsMenu>
                {showInPageType && <InPagePersonalizationOptions />}
                {showCustomerSettings && <CustomerSettings />}
              </PersonalizationSettingsMenu>
            );
          })()}
        </>
      )}
      {/* Above everything the author acts on, because it is a statement about
          the design as a whole: which layers hold one value between them,
          wherever they sit. The list further down shows one spread at a time,
          so a group reaching the other side of the card would otherwise have
          nowhere to be seen from. It renders nothing until something is
          shared, and `.section:empty` collapses the block with it — the same
          rule that keeps "Other layers" from leaving a bordered gap. */}
      <ContentBlock className={styles.section}>
        <SharedValuesSection />
      </ContentBlock>
      <ContentBlock className={styles.section}>
        <ButtonAddLayer
          showCondition
          showCollection={showCollection}
          isPersonalized
          title={t('Add Personalized Layer')}
        />
      </ContentBlock>
      <ContentBlock className={styles.section}>
        {personalizedElements?.length !== 0 && (
          <div className={cn(styles.sectionHeader, styles.personalizedLayerHeader)}>
            {t('Personalized layers')}
            <div className={cn('mt-1', styles.personalizedLayerInfo)}>
              {t('How personalization options will be displayed on your product page')}
            </div>
          </div>
        )}
        <ConditionGroupBuilder conditionGroup={conditionGroup} />
        <ReflowGroupsList />
      </ContentBlock>
      <ContentBlock className={styles.section}>
        <NonPersonalizableElements personalizedElements={personalizedElements} />
      </ContentBlock>
    </div>
  );
}

export default PersonalizationTabContent;
